import {
  FormatBoldOutlineIcon,
  FormatListNumberedOutlineIcon,
  FormatListBulletedOutlineIcon,
  FormatItalicOutlineIcon,
  AccessibilityFillIcon,
} from '@applyboard/ui-icons';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { isKeyHotkey } from 'is-hotkey';
import { Transforms, createEditor, Range, Editor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, withReact, Slate, RenderElementProps, RenderLeafProps } from 'slate-react';
import { withHtmlPaste } from './RichTextEditor.html';
import { Intent, useFullTheme } from '~/design';
import { assert, useId } from '~/utils';
import { CustomEditor } from './slate';
import { BlockButton, FontsWrapper, LinkWrapper, MarkButton } from './RichTextEditor.wrappers';
import {
  addSoftLineBreakIfNeeded,
  convertMarkdownStringToSlateTree,
  convertSlateTreeToMarkdownString,
  escapeBlockquoteIfNeeded,
  escapeHeadingIfNeeded,
  escapeListIfNeeded,
  getHeadingsConfig,
  handleHotkeys,
} from './RichTextEditor.utils';
import { Alerts, IconButton, ToolbarContext } from './RichTextEditor.toolbar';
import { FieldCompatibleProps } from '../Field';
import { Label } from '~/internal';
import { HelpText } from '../HelpText';
import { Text } from '../Text';
import { getHeadingVariant, validateAccessibleHeadingStyles } from '../Markdown';
import { unstable_batchedUpdates, flushSync } from 'react-dom';
import { SubtleNotice } from '../Markdown/SubtleNotice';
import { Flex } from '../Flex';
import { ACCESSIBILITY_ERROR_MESSAGE, AccessibilityPanel } from './AccessibilityPanel';
import { PowerFeaturesConfig } from './RichTextEditor.types';
import { useRichTextEditor } from '~/internal/RichTextEditorContext';
import { Textarea } from '../Textarea';
import { Link } from '../Link';

export interface RichTextEditorProps extends FieldCompatibleProps<string> {
  /** The aria-label of the text field. */
  'aria-label'?: string;

  /** The aria-labelledby of the text field. */
  'aria-labelledby'?: string;

  /**
   * Accepts the ID of an element on the screen with text that describes
   * the rich text editor
   */
  'aria-describedby'?: string;

  /** If true, the text field cannot be interacted with. */
  disabled?: boolean;

  /**
   * Enable additional features for power users
   * - `alerts`: Alerts allow you to show colorful notices to users
   * - `headings`: Headings can be used to organize content and improve readability.
   *   - **Note**: Enabling `headings` will also automatically enable the accessibility panel
   *               to help the user ensure the heading structure is accessible
   *   - **Options**:
   *     - `maxLevel` - Sets the maximum heading level that users are allowed to select.
   *                    Since HTML headings only go up to h6, this should typically be set
   *                    to a lower level like 2-3, to leave room to render 1-3 headings above
   *                    the content.  Defaults to 3
   *     - `styles`   - Allows customization of heading styles. You can specify different styles
   *                    for each heading level (h1 to h6). Each style can include properties like
   *                    `variant`, `contrast`, and `bold`.
   *
   */
  powerFeatures?: PowerFeaturesConfig;
}

export function RichTextEditor({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedBy,
  disabled,
  helpText,
  invalid,
  label,
  onChange,
  required,
  value,
  powerFeatures,
}: RichTextEditorProps) {
  const [editorValue, setEditorValue] = React.useState(value);
  const renderElement = useCallback(
    props => <Element powerFeatures={powerFeatures} {...props} />,
    [powerFeatures],
  );
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);
  const headingsConfigForEditor = getHeadingsConfig(powerFeatures);
  const allowHeadings = !!headingsConfigForEditor;
  const maxHeadingLevel = headingsConfigForEditor?.maxLevel;
  const editor: CustomEditor = useMemo(() => {
    const base = createEditor();
    const instance = withReact(base);
    const withHistoryInstance = withHistory(instance);
    return withHtmlPaste(withInlines(withHistoryInstance), {
      maxHeadingLevel,
      allowHeadings,
    });
  }, [maxHeadingLevel, allowHeadings]);
  const { borders, borderRadiuses, colors, spacing } = useFullTheme();
  const { forcePlainTextEditor } = useRichTextEditor();
  const id = useId();
  const helpTextId = useId();
  const labelId = useId();
  const adjustedIntent = invalid ? 'negative' : 'secondary';

  if (!label && !ariaLabel && !ariaLabelledby) {
    throw new Error(
      'One of `label`, `aria-label`, or `aria-labelledby` is required for accessibility reasons.',
    );
  }

  if ([label, ariaLabel, ariaLabelledby].filter(Boolean).length > 1) {
    throw new Error('Only one of `label`, `aria-label`, `aria-labelledby` can be used.');
  }

  const headingConfig = getHeadingsConfig(powerFeatures);
  useMemo(() => validateAccessibleHeadingStyles(headingConfig), [headingConfig]);

  const valueAsSlateTree = useMemo(() => convertMarkdownStringToSlateTree(value), [value]);

  if (editorValue !== value) {
    // This occurs if the value is changed externally
    unstable_batchedUpdates(() => {
      setEditorValue(value);
      editor.history = { redos: [], undos: [] };
      editor.children = convertMarkdownStringToSlateTree(value);
      const startPoint = Editor.start(editor, []);
      editor.selection = { anchor: startPoint, focus: startPoint };
    });
  }

  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [accessibilityPanelOpen, setAccessibilityPanelOpen] = useState(false);

  const [prevHelpText, setPrevHelpText] = useState(helpText);
  if (helpText !== prevHelpText) {
    setPrevHelpText(helpText);
    if (helpText === ACCESSIBILITY_ERROR_MESSAGE) {
      setAccessibilityPanelOpen(true);
    }
  }

  if (forcePlainTextEditor) {
    return (
      <Textarea
        label={label}
        onChange={onChange}
        value={value}
        invalid={invalid}
        helpText={helpText}
      />
    );
  }

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[2],
        width: '100%',
      }}
    >
      <Label disabled={disabled} id={labelId} label={label} required={required} />

      <div
        css={[
          !disabled && invalid ? borders.negative : borders.inactive,
          {
            backgroundColor: colors.bgRegular,
            borderRadius: borderRadiuses.control,
            width: '100%',
            position: 'relative',
            containerType: 'inline-size',
            overflow: 'hidden',
            '&:hover': !disabled && {
              borderColor: invalid ? undefined : borders.secondaryHover.borderColor,
              backgroundColor: colors.bgRegularHover,
            },
          },
          !disabled && {
            '&:focus-within': {
              borderColor: 'transparent',
              outlineColor: invalid ? colors.actionNegative : colors.actionPrimary,
              outlineStyle: 'solid',
              outlineWidth: spacing['2px'],
            },
          },
          disabled && {
            backgroundColor: colors.bgInactive,
            '&:focus-within': {
              outlineWidth: 0,
            },
          },
        ]}
      >
        <Slate
          editor={editor}
          initialValue={valueAsSlateTree}
          onChange={val => {
            const result = convertSlateTreeToMarkdownString(val).replace(/<!---->/g, '');
            unstable_batchedUpdates(() => {
              setEditorValue(result);
              onChange(result);
            });
          }}
        >
          <Flex align="stretch">
            <Flex.Item grow={1}>
              <Toolbar
                disabled={disabled}
                onOpenAccessibilityPanel={() => {
                  flushSync(() => {
                    setAccessibilityPanelOpen(true);
                  });
                  closeButtonRef.current?.focus();
                }}
                showAccessibilityPanelButton={!accessibilityPanelOpen}
                accessibilityPanelButtonRef={openButtonRef}
                powerFeatures={powerFeatures}
              />

              <Editable
                aria-describedby={
                  helpText
                    ? [helpTextId, ariaDescribedBy].filter(Boolean).join(' ')
                    : ariaDescribedBy
                }
                aria-invalid={invalid}
                aria-label={ariaLabel ? ariaLabel : undefined}
                aria-labelledby={label ? labelId : ariaLabelledby ? ariaLabelledby : undefined}
                aria-required={required ? 'true' : undefined}
                css={[
                  {
                    borderTopColor: borders.inactive.borderColor,
                    borderTopStyle: borders.inactive.borderStyle,
                    borderTopWidth: borders.inactive.borderWidth,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[4],
                    outline: 0,
                    paddingTop: spacing[3],
                    paddingInline: spacing[4],
                    paddingBottom: spacing[4],
                  },
                  disabled && {
                    color: colors.textInactive,
                    cursor: 'not-allowed',
                  },
                ]}
                id={id}
                onKeyDown={event => {
                  if (disabled) {
                    return;
                  }

                  escapeHeadingIfNeeded(editor, event);
                  addSoftLineBreakIfNeeded(editor, event);
                  escapeBlockquoteIfNeeded(editor, event);
                  escapeListIfNeeded(editor, event);
                  handleHotkeys(editor, event);

                  const { selection } = editor;

                  // Copied from: https://github.com/ianstormtaylor/slate/blob/main/site/examples/ts/inlines.tsx#L74
                  // Necessary so the user can move left of `link` elements with keyboard
                  if (selection && Range.isCollapsed(selection)) {
                    const { nativeEvent } = event;
                    if (isKeyHotkey('left', nativeEvent)) {
                      event.preventDefault();
                      Transforms.move(editor, { unit: 'offset', reverse: true });
                      return;
                    }
                    if (isKeyHotkey('right', nativeEvent)) {
                      event.preventDefault();
                      Transforms.move(editor, { unit: 'offset' });
                      return;
                    }
                  }
                }}
                readOnly={disabled}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                spellCheck
              />
            </Flex.Item>
            <AccessibilityPanel
              open={accessibilityPanelOpen}
              onClose={() => {
                flushSync(() => {
                  setAccessibilityPanelOpen(false);
                });
                openButtonRef.current?.focus();
              }}
              closeButtonRef={closeButtonRef}
            />
          </Flex>
        </Slate>
      </div>

      {helpText && (
        <HelpText id={helpTextId} intent={adjustedIntent} showIcon={invalid || disabled}>
          {helpText}
        </HelpText>
      )}
    </div>
  );
}

function Toolbar({
  disabled,
  onOpenAccessibilityPanel,
  showAccessibilityPanelButton,
  accessibilityPanelButtonRef,
  powerFeatures,
}: {
  disabled?: boolean;
  onOpenAccessibilityPanel: () => void;
  showAccessibilityPanelButton?: boolean;
  accessibilityPanelButtonRef?: React.RefObject<HTMLButtonElement>;
  powerFeatures?: RichTextEditorProps['powerFeatures'];
}) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const { spacing } = useFullTheme();
  const headingsConfig = getHeadingsConfig(powerFeatures);

  return (
    <ToolbarContext.Provider value={{ disabled, toolbarRef }}>
      <div
        aria-label="Controls for marking up text"
        css={{
          display: 'flex',
          gap: spacing[2],
          padding: spacing[2],
          backgroundColor: 'white',
        }}
        ref={toolbarRef}
        role="toolbar"
      >
        {headingsConfig && <FontsWrapper headingsConfig={headingsConfig} />}
        {powerFeatures?.some(item => item === 'alerts') && <Alerts />}
        <MarkButton format="strong" icon={FormatBoldOutlineIcon} label="Toggle bold" />
        <MarkButton format="emphasis" icon={FormatItalicOutlineIcon} label="Toggle italic" />
        <BlockButton format="ol" icon={FormatListNumberedOutlineIcon} label="Toggle ordered list" />
        <BlockButton
          format="ul"
          icon={FormatListBulletedOutlineIcon}
          label="Toggle unordered list"
        />
        <LinkWrapper />
        {showAccessibilityPanelButton && headingsConfig && (
          <IconButton
            aria-label="Open accessibility checker"
            icon={AccessibilityFillIcon}
            onClick={onOpenAccessibilityPanel}
            ref={accessibilityPanelButtonRef}
          />
        )}
      </div>
    </ToolbarContext.Provider>
  );
}

const Element = ({
  attributes,
  children,
  element,
  powerFeatures,
}: RenderElementProps & { powerFeatures?: RichTextEditorProps['powerFeatures'] }) => {
  const { colors, spacing } = useFullTheme();
  const style: React.CSSProperties = {
    fontFamily: 'Open Sans',
    margin: 0,
  };
  const headingConfig = getHeadingsConfig(powerFeatures);

  switch (element.type) {
    case 'heading': {
      assert(
        element.depth === 1 ||
          element.depth === 2 ||
          element.depth === 3 ||
          element.depth === 4 ||
          element.depth === 5 ||
          element.depth === 6,
        'Missing element.depth',
      );

      const headingStyle = headingConfig?.styles?.[`h${element.depth}`];

      return (
        <div
          css={({ spacing }) => ({
            // Headings should be close to the content that follows them, so it's more
            // clear the heading is "labelling" the content below it
            marginBottom: `-${spacing[3]}`,
          })}
        >
          <Text
            variant={headingStyle?.variant || getHeadingVariant(element.depth)}
            contrast={headingStyle?.contrast}
          >
            <span style={{ fontWeight: headingStyle?.bold ? 700 : undefined }}>{children}</span>
          </Text>
        </div>
      );
    }
    case 'link':
      return (
        <Text as="span">
          <Link href={element.url} variant="inline">
            <span {...attributes}>
              <InlineChromiumBugfix />
              {children}
              <InlineChromiumBugfix />
            </span>
          </Link>
        </Text>
      );
    case 'li':
      return (
        <li style={{ ...style, color: colors.textSecondaryStark }} {...attributes}>
          {children}
        </li>
      );
    case 'ol':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    case 'blockquote':
      if (element.importance) {
        return (
          <SubtleNotice importance={element.importance}>
            <div {...attributes}>
              <Flex gap={4} direction="column">
                {children}
              </Flex>
            </div>
          </SubtleNotice>
        );
      }

      return (
        <blockquote
          style={{
            ...style,
            borderLeftColor: colors.borderInactive,
            borderLeftStyle: 'solid',
            borderLeftWidth: '2px',
            marginLeft: 0,
            paddingLeft: spacing[4],
          }}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case 'ul':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    default:
      return (
        <Text as="p" variant="bodyM">
          <span style={style} {...attributes}>
            {children}
          </span>
        </Text>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  const { colors } = useFullTheme();
  const style: React.CSSProperties = {};

  if (leaf.bold || leaf.strong) {
    children = <strong>{children}</strong>;
  }
  if (leaf.code) {
    children = <code>{children}</code>;
  }
  if (leaf.italic || leaf.emphasis) {
    children = <em>{children}</em>;
  }
  if (leaf.strikethrough) {
    children = <s>{children}</s>;
  }
  if (leaf.underline) {
    style.textDecoration = 'underline';
  }
  if (leaf.color) {
    style.color = {
      magic: colors.textMagic,
      negative: colors.textNegative,
      positive: colors.textPositive,
      primary: colors.textPrimary,
      secondary: colors.textSecondaryStark,
      warning: colors.textWarning,
    }[leaf.color as Intent];
    if (leaf.highlight) {
      style.backgroundColor = colors.bgWarning;
    }
  }

  return (
    <span {...attributes} style={style}>
      {children}
    </span>
  );
};

function withInlines(editor: CustomEditor) {
  const { isInline } = editor;

  editor.isInline = element => ['link'].includes(element.type) || isInline(element);

  return editor;
}

// Put this at the start and end of an inline component to work around this Chromium bug:
// https://bugs.chromium.org/p/chromium/issues/detail?id=1249405
const InlineChromiumBugfix = () => (
  <span contentEditable={false} css={{ fontSize: 0 }}>
    {String.fromCodePoint(160) /* Non-breaking space */}
  </span>
);
